import React from 'react';
import { useAdminDashboardStats } from '@/hooks/useAdminDashboardStats';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Package, DollarSign, Warehouse, Users, Wheat, Bell,
  AlertTriangle, TrendingUp, TrendingDown, Clock,
  ArrowRight, ShieldCheck, Boxes, Plus, CreditCard,
  BarChart3, ClipboardCheck, ArrowUpRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell
} from 'recharts';

const AdminDashboard: React.FC = () => {
  const { data: stats, isLoading } = useAdminDashboardStats();
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading || !stats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28 w-full rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      </div>
    );
  }

  const totalPipelineCount = Object.values(stats.procurementsByStatus).reduce((s, v) => s + v, 0);
  const activeProcurements = totalPipelineCount - (stats.procurementsByStatus['completed'] || 0) - (stats.procurementsByStatus['rejected'] || 0);
  const totalPendingActions = (stats.procurementsByStatus['pending_finance'] || 0) + stats.pendingPaymentRequests + stats.lowStockCount + stats.outOfStockCount + stats.pendingQualityChecks + stats.pendingGRNCount;

  const expenseChange = stats.totalExpensesLastMonth > 0
    ? ((stats.totalExpensesThisMonth - stats.totalExpensesLastMonth) / stats.totalExpensesLastMonth * 100)
    : null;

  const b2bChange = stats.b2bSalesLastMonth > 0
    ? ((stats.b2bSalesThisMonth - stats.b2bSalesLastMonth) / stats.b2bSalesLastMonth * 100)
    : null;

  const formatBr = (v: number) => {
    if (v >= 1_000_000) return `Br ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `Br ${(v / 1_000).toFixed(0)}K`;
    return `Br ${v.toLocaleString()}`;
  };

  // Pie chart data for procurement status
  const pieData = [
    { name: 'Initiated', value: stats.procurementsByStatus['initiated'] || 0, color: 'hsl(var(--muted-foreground))' },
    { name: 'Pending Finance', value: stats.procurementsByStatus['pending_finance'] || 0, color: 'hsl(var(--chart-3))' },
    { name: 'Pending Warehouse', value: stats.procurementsByStatus['pending_warehouse'] || 0, color: 'hsl(var(--accent))' },
    { name: 'Completed', value: stats.procurementsByStatus['completed'] || 0, color: 'hsl(var(--primary))' },
    { name: 'Rejected', value: stats.procurementsByStatus['rejected'] || 0, color: 'hsl(var(--destructive))' },
  ].filter(d => d.value > 0);

  // Action items
  const actionItems = [
    stats.procurementsByStatus['pending_finance'] ? { label: `${stats.procurementsByStatus['pending_finance']} procurements need finance approval`, path: '/finance/approvals/pending', urgent: true } : null,
    stats.pendingPaymentRequests ? { label: `${stats.pendingPaymentRequests} payment requests pending (${formatBr(stats.pendingPaymentAmount)})`, path: '/finance/accounting/payment-requests', urgent: true } : null,
    stats.approvedAwaitingPayment ? { label: `${stats.approvedAwaitingPayment} approved requests awaiting payment`, path: '/finance/payments/payment-requests', urgent: false } : null,
    stats.pendingGRNCount ? { label: `${stats.pendingGRNCount} GRNs pending processing`, path: '/warehouse', urgent: false } : null,
    stats.procurementsByStatus['pending_warehouse'] ? { label: `${stats.procurementsByStatus['pending_warehouse']} procurements pending warehouse`, path: '/warehouse', urgent: false } : null,
    stats.outOfStockCount ? { label: `${stats.outOfStockCount} products out of stock`, path: '/inventory', urgent: true } : null,
    stats.lowStockCount ? { label: `${stats.lowStockCount} products at low stock`, path: '/inventory', urgent: false } : null,
    stats.pendingQualityChecks ? { label: `${stats.pendingQualityChecks} pending quality checks`, path: '/quality', urgent: false } : null,
  ].filter(Boolean) as { label: string; path: string; urgent: boolean }[];

  const statusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'default' as const;
      case 'pending_finance': case 'pending_warehouse': return 'secondary' as const;
      case 'rejected': return 'destructive' as const;
      default: return 'outline' as const;
    }
  };

  // Module quick-access
  const modules = [
    { name: 'Procurement', icon: Package, path: '/procurement', metric: `${activeProcurements} active`, color: 'border-l-primary' },
    { name: 'Finance', icon: DollarSign, path: '/finance', metric: `${stats.pendingPaymentRequests} pending`, color: 'border-l-chart-3' },
    { name: 'Inventory', icon: Boxes, path: '/inventory', metric: `${stats.totalProducts} items`, color: 'border-l-accent' },
    { name: 'Farms', icon: Wheat, path: '/farms', metric: `${stats.totalFarms} farms`, color: 'border-l-chart-2' },
    { name: 'Quality', icon: ShieldCheck, path: '/quality', metric: `${stats.pendingQualityChecks} checks`, color: 'border-l-chart-4' },
    { name: 'Users', icon: Users, path: '/users', metric: `${stats.totalUsers} users`, color: 'border-l-chart-5' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{getGreeting()}, Admin</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {totalPendingActions > 0 && (
                  <span className="ml-2 text-foreground font-medium">
                    · {totalPendingActions} items need your attention
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => navigate('/procurement/new')} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New Procurement
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/finance/approvals/pending')} className="gap-1.5">
                <ClipboardCheck className="h-3.5 w-3.5" /> Approve Payments
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/inventory')} className="gap-1.5">
                <Boxes className="h-3.5 w-3.5" /> Inventory
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          label="Active Procurements"
          value={activeProcurements}
          icon={Package}
          accentClass="border-l-primary"
          sub={`${stats.procurementsByStatus['pending_finance'] || 0} awaiting finance`}
          onClick={() => navigate('/procurement')}
        />
        <KPICard
          label="Pending Approvals"
          value={(stats.procurementsByStatus['pending_finance'] || 0) + stats.pendingPaymentRequests}
          icon={Clock}
          accentClass="border-l-chart-3"
          sub={`${stats.pendingPaymentRequests} payment requests`}
          alert={(stats.procurementsByStatus['pending_finance'] || 0) + stats.pendingPaymentRequests > 0}
          onClick={() => navigate('/finance/approvals/pending')}
        />
        <KPICard
          label="Expenses This Month"
          value={formatBr(stats.totalExpensesThisMonth)}
          icon={CreditCard}
          accentClass="border-l-chart-1"
          sub={expenseChange !== null ? `${expenseChange > 0 ? '+' : ''}${expenseChange.toFixed(1)}% vs last month` : 'No prior data'}
          trend={expenseChange !== null ? (expenseChange > 0 ? 'up' : 'down') : undefined}
          onClick={() => navigate('/finance/accounting/expenses')}
        />
        <KPICard
          label="Procurement Value"
          value={formatBr(stats.monthlyProcurementValue)}
          icon={TrendingUp}
          accentClass="border-l-chart-2"
          sub="This month"
          onClick={() => navigate('/procurement')}
        />
        <KPICard
          label="B2B Sales"
          value={formatBr(stats.b2bSalesThisMonth)}
          icon={BarChart3}
          accentClass="border-l-chart-4"
          sub={b2bChange !== null ? `${b2bChange > 0 ? '+' : ''}${b2bChange.toFixed(1)}% vs last month` : 'This month'}
          trend={b2bChange !== null ? (b2bChange > 0 ? 'up' : 'down') : undefined}
          onClick={() => navigate('/sales/b2b')}
        />
      </div>

      {/* Action Alert Banners */}
      {actionItems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {actionItems.slice(0, 4).map((item, i) => (
            <button
              key={i}
              onClick={() => navigate(item.path)}
              className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all hover:shadow-sm ${
                item.urgent
                  ? 'border-l-4 border-l-destructive bg-destructive/5 hover:bg-destructive/10'
                  : 'border-l-4 border-l-chart-3 bg-chart-3/5 hover:bg-chart-3/10'
              }`}
            >
              <AlertTriangle className={`h-4 w-4 shrink-0 ${item.urgent ? 'text-destructive' : 'text-chart-3'}`} />
              <span className="text-sm font-medium flex-1">{item.label}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Procurement Trend */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Procurement Trend (6 Months)
              </CardTitle>
              <button onClick={() => navigate('/procurement')} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                Details <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.procurementTrend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="procGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value: number) => [`Br ${value.toLocaleString()}`, 'Value']}
                  />
                  <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#procGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Procurement Status Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-accent" />
                Pipeline Breakdown
              </CardTitle>
              <span className="text-xs text-muted-foreground">{totalPipelineCount} total</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="h-48 w-48 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5 flex-1">
                {pieData.map((d) => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="text-xs text-muted-foreground flex-1">{d.name}</span>
                    <span className="text-sm font-semibold tabular-nums">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Procurements + Financial Snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Procurements Table */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                Recent Procurements
              </CardTitle>
              <button onClick={() => navigate('/procurement')} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {stats.recentProcurements.slice(0, 6).map((p: any) => (
                <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.product_name || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{p.source_type} · {p.total_quantity || 0} kg</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold tabular-nums">
                      Br {(p.total_amount || 0).toLocaleString()}
                    </span>
                    <Badge variant={statusBadgeVariant(p.status)} className="text-[10px]">
                      {p.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
              ))}
              {stats.recentProcurements.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">No procurements yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right sidebar metrics */}
        <div className="space-y-4">
          {/* Financial Snapshot */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-chart-1" />
                Financial Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetricRow label="Total Procurement Value" value={formatBr(stats.totalProcurementValue)} />
              <MetricRow label="Expenses This Month" value={formatBr(stats.totalExpensesThisMonth)} />
              <MetricRow label="B2B Sales This Month" value={formatBr(stats.b2bSalesThisMonth)} />
              {expenseChange !== null && (
                <div className="flex justify-between items-center pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">Expense MoM</span>
                  <span className={`text-sm font-semibold flex items-center gap-1 ${expenseChange > 0 ? 'text-destructive' : 'text-primary'}`}>
                    {expenseChange > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {expenseChange > 0 ? '+' : ''}{expenseChange.toFixed(1)}%
                  </span>
                </div>
              )}
              {stats.latestExchangeRate && (
                <div className="flex justify-between items-center pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">{stats.latestExchangeRate.currency_pair}</span>
                  <span className="text-sm font-semibold tabular-nums">{stats.latestExchangeRate.rate.toFixed(2)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inventory Health */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Warehouse className="h-4 w-4 text-accent" />
                Inventory Health
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <MetricRow label="Total Products" value={String(stats.totalProducts)} />
              <MetricRow label="Low Stock" value={String(stats.lowStockCount)} valueClass={stats.lowStockCount > 0 ? 'text-chart-3' : ''} />
              <MetricRow label="Out of Stock" value={String(stats.outOfStockCount)} valueClass={stats.outOfStockCount > 0 ? 'text-destructive' : ''} />
              {stats.totalProducts > 0 && (
                <div className="pt-2 border-t border-border/50">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>Stock health</span>
                    <span className="font-medium">{Math.round(((stats.totalProducts - stats.lowStockCount - stats.outOfStockCount) / stats.totalProducts) * 100)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${((stats.totalProducts - stats.lowStockCount - stats.outOfStockCount) / stats.totalProducts) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Module Quick-Access Grid */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Access</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {modules.map((mod) => (
            <button
              key={mod.name}
              onClick={() => navigate(mod.path)}
              className={`group flex flex-col items-start gap-2 p-4 rounded-lg border border-l-4 ${mod.color} bg-card hover:shadow-md transition-all text-left`}
            >
              <mod.icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              <div>
                <p className="text-sm font-semibold">{mod.name}</p>
                <p className="text-xs text-muted-foreground">{mod.metric}</p>
              </div>
              <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors self-end" />
            </button>
          ))}
        </div>
      </div>

      {/* System Alerts */}
      {stats.criticalAlerts.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bell className="h-4 w-4 text-destructive" />
              System Alerts ({stats.criticalAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {stats.criticalAlerts.map((alert: any) => (
              <div key={alert.id} className="flex items-start gap-2 p-2.5 rounded-md bg-card border">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium capitalize">{alert.type}</p>
                  <p className="text-xs text-muted-foreground truncate">{alert.message}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Sub-components
function KPICard({ label, value, icon: Icon, accentClass, sub, onClick, alert, trend }: {
  label: string;
  value: string | number;
  icon: any;
  accentClass: string;
  sub?: string;
  onClick?: () => void;
  alert?: boolean;
  trend?: 'up' | 'down';
}) {
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all border-l-4 ${accentClass} ${alert ? 'ring-1 ring-chart-3/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-muted-foreground font-medium truncate">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground/60" />
        </div>
        <p className="text-2xl font-bold tabular-nums tracking-tight">{value}</p>
        {sub && (
          <p className={`text-[11px] mt-1 ${trend === 'up' ? 'text-destructive' : trend === 'down' ? 'text-primary' : 'text-muted-foreground'}`}>
            {sub}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueClass || ''}`}>{value}</span>
    </div>
  );
}

export default AdminDashboard;
